import { ArrowRight, Plus } from 'lucide-react';

import logoUrl from '@/assets/sancus-logo.svg';
import { LEGAL_ENTITY } from '@/lib/legal-entity';

import { sections } from './landing-header';
import { SectionLabel } from './section-label';

const questions = [
  [
    'O Sancus serve para quem advoga sozinho?',
    'Sim. Você pode começar organizando a sua própria rotina e convidar a equipe quando o escritório crescer. Cada pessoa entra com o perfil adequado: sócio, administrador, advogado, estagiário ou secretária.',
  ],
  [
    'Como funciona o monitoramento de publicações?',
    'Você cadastra as OABs do escritório e o Sancus acompanha o DJEN. Cada publicação é vinculada ao processo pelo número CNJ, e as que não casam vão para uma fila de triagem. A IA pode sugerir o prazo a partir do teor da intimação, e nada é salvo sem a confirmação de um advogado.',
  ],
  [
    'Posso confiar no cálculo de prazos?',
    'O cálculo segue o CPC (arts. 219, 220 e 224), considera os feriados nacionais e móveis e o recesso forense, e aceita os feriados locais e as suspensões do seu tribunal. Cada prazo traz a memória do cálculo, com os dias que não contaram e o motivo, para você conferir antes de confirmar.',
  ],
  [
    'A inteligência artificial acessa os dados dos meus clientes?',
    'A Lexi, que ajuda a usar o sistema, não acessa os dados do escritório nem guarda a conversa. Nas publicações, a IA analisa o texto da intimação apenas para sugerir o prazo, e a decisão é sempre sua.',
  ],
  [
    'Como o Sancus protege as informações do escritório?',
    'Os dados de cada escritório ficam isolados em três camadas, inclusive no banco de dados. Há verificação em duas etapas (obrigatória para sócios e administradores), permissões por perfil, trilha de auditoria imutável, antivírus e criptografia nos documentos e ferramentas para os pedidos de titulares da LGPD.',
  ],
  [
    'Como faço para começar?',
    'Clique em “Criar conta gratuita”, preencha seus dados e os do escritório e confirme o e-mail. Depois, cadastre as OABs para as publicações começarem a chegar.',
  ],
] as const;

export function FaqSection() {
  return (
    <section
      id="duvidas"
      aria-labelledby="lp-faq-title"
      className="scroll-mt-20 bg-white py-24 sm:py-32"
    >
      <div className="mx-auto grid max-w-[1180px] gap-10 px-5 sm:px-6 lg:grid-cols-[1fr_1.4fr] lg:gap-16">
        <div data-reveal>
          <SectionLabel>Dúvidas</SectionLabel>
          <h2
            id="lp-faq-title"
            className="text-(--lp-mute) mt-5 text-[clamp(32px,4.2vw,48px)] font-medium leading-[1.08] tracking-[-0.04em]"
          >
            Perguntas frequentes, <span className="text-(--lp-ink)">respostas diretas.</span>
          </h2>
        </div>
        <div className="divide-(--lp-line) border-(--lp-line) divide-y border-y" data-reveal>
          {questions.map(([question, answer]) => (
            <details key={question} name="lp-faq" className="group">
              <summary className="text-(--lp-ink) flex cursor-pointer list-none items-center justify-between gap-6 py-5 text-[17px] font-medium [&::-webkit-details-marker]:hidden">
                {question}
                <span className="border-(--lp-line) group-open:border-(--lp-ink) group-open:bg-(--lp-ink) grid size-9 shrink-0 place-items-center rounded-full border transition-colors group-open:text-white">
                  <Plus
                    size={17}
                    aria-hidden="true"
                    className="transition-transform duration-200 group-open:rotate-45"
                  />
                </span>
              </summary>
              <p className="text-(--lp-body) max-w-[640px] pb-6 text-[15.5px] leading-relaxed">
                {answer}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FinalCta() {
  return (
    <section aria-labelledby="lp-final-title" className="bg-white px-3 pb-3 sm:px-6 sm:pb-6">
      <div
        data-reveal
        className="lp-diagonals bg-primary relative mx-auto max-w-[1280px] overflow-hidden rounded-[32px] px-6 py-20 text-center sm:py-28"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[radial-gradient(60%_80%_at_50%_0%,rgb(255_255_255/0.22),transparent)]"
        />
        <h2
          id="lp-final-title"
          className="relative mx-auto max-w-[760px] text-balance text-[clamp(34px,5vw,60px)] font-medium leading-[1.05] tracking-[-0.045em] text-white/60"
        >
          Comece em minutos.{' '}
          <span className="text-white">Seu escritório em dia a partir de hoje.</span>
        </h2>
        <p className="relative mx-auto mt-5 max-w-[480px] text-[17px] text-white/80">
          Crie a conta, cadastre suas OABs e veja as publicações chegando.
        </p>
        <div className="relative mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href="/criar-conta"
            className="text-(--lp-ink) inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-white px-6 text-[15px] font-medium transition-transform hover:-translate-y-px sm:w-auto"
          >
            Criar conta gratuita <ArrowRight size={17} />
          </a>
          <a
            href="/entrar"
            className="inline-flex h-12 items-center gap-1.5 px-4 text-[15px] font-medium text-white/80 transition-colors hover:text-white"
          >
            Já tenho conta <ArrowRight size={15} />
          </a>
        </div>
      </div>
    </section>
  );
}

export function LandingFooter() {
  return (
    <footer className="overflow-hidden bg-white">
      <div className="mx-auto grid max-w-[1180px] gap-10 px-5 pb-10 pt-16 sm:px-6 md:grid-cols-[2fr_1fr_1fr_1fr]">
        <div>
          <img
            src={logoUrl}
            width="280"
            height="100"
            alt="Sancus Advogados"
            className="h-10 w-auto"
          />
          <p className="text-(--lp-body) mt-4 max-w-[320px] text-[15px] leading-relaxed">
            CRM jurídico para escritórios de advocacia. Publicações, prazos, clientes, documentos e
            honorários em um só lugar.
          </p>
        </div>
        <nav aria-label="Produto" className="flex flex-col gap-2.5 text-[15px]">
          <span className="text-(--lp-ink) mb-1 text-[14px] font-medium">Produto</span>
          {sections.map(([label, href]) => (
            <a key={href} href={href} className="text-(--lp-body) hover:text-(--lp-ink) w-fit">
              {label}
            </a>
          ))}
        </nav>
        <nav aria-label="Acesso" className="flex flex-col gap-2.5 text-[15px]">
          <span className="text-(--lp-ink) mb-1 text-[14px] font-medium">Acesso</span>
          <a href="/entrar" className="text-(--lp-body) hover:text-(--lp-ink) w-fit">
            Entrar
          </a>
          <a href="/criar-conta" className="text-(--lp-body) hover:text-(--lp-ink) w-fit">
            Criar conta gratuita
          </a>
        </nav>
        <nav aria-label="Legal" className="flex flex-col gap-2.5 text-[15px]">
          <span className="text-(--lp-ink) mb-1 text-[14px] font-medium">Legal</span>
          <a
            href="/politica-de-privacidade"
            className="text-(--lp-body) hover:text-(--lp-ink) w-fit"
          >
            Política de Privacidade
          </a>
          <a
            href="/politica-de-privacidade#exclusao-de-dados"
            className="text-(--lp-body) hover:text-(--lp-ink) w-fit"
          >
            Exclusão de dados
          </a>
        </nav>
      </div>
      <div className="border-(--lp-line) text-(--lp-mute) mx-auto flex max-w-[1180px] flex-wrap justify-between gap-2 border-t px-5 py-6 text-[13.5px] sm:px-6">
        <span>
          © {new Date().getFullYear()} {LEGAL_ENTITY.legalName} · CNPJ {LEGAL_ENTITY.cnpj}. Todos os
          direitos reservados.
        </span>
        <span>Feito no Brasil, para a advocacia brasileira.</span>
      </div>
      <div
        aria-hidden="true"
        className="text-(--lp-soft) -mb-[0.18em] select-none text-center text-[clamp(96px,24vw,360px)] font-semibold leading-none tracking-[-0.07em]"
      >
        Sancus
      </div>
    </footer>
  );
}
